import { memo } from 'react';
import { Link } from 'react-router-dom';
import { trackCTAClick } from '@/hooks/useGoogleAnalytics';
import { openCookieSettings } from '@/hooks/useCookieConsent';
import logoFooter from '@/assets/logo-footer.svg';

const prefetchPage = (path: string) => {
  // Prefetch on hover for faster navigation
  import(`../pages/${path}.tsx`).catch(() => {});
};

const Footer = memo(() => {
  const currentYear = new Date().getFullYear();
  return <footer className="bg-primary text-primary-foreground">
      <div className="container-custom py-16">
        <div className="grid md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <p className="text-2xl font-semibold mb-4">Marina González Hurtado</p>
            <p className="text-white/70 max-w-sm mb-6">
              Nutricionista colegiada dedicada a ayudarte a alcanzar tus objetivos
              de salud con planes personalizados y seguimiento continuo.
            </p>
            <img 
              src={logoFooter} 
              alt="Logo de MGH Nutrición - Marina González Hurtado Nutricionista" 
              className="w-full h-auto max-w-xs"
              width={320}
              height={117}
              loading="lazy"
            />
          </div>

          {/* Quick Links */}
          <nav aria-label="Enlaces de navegación del pie de página">
            <p className="font-semibold mb-4">Enlaces rápidos</p>
            <ul className="space-y-3">
              {[{
              href: '#inicio',
              label: 'Inicio'
            }, {
              href: '#servicios',
              label: 'Servicios'
            }, {
              href: '#sobre-mi',
              label: 'Sobre mí'
            }, {
              href: '#resenas',
              label: 'Reseñas'
            }, {
              href: '#precios',
              label: 'Precios'
            }, {
              href: '#contacto',
              label: 'Contacto'
            }, {
              href: '#faq',
              label: 'FAQs'
            }].map(link => <li key={link.href}>
                  <a
                    href={link.href}
                    className="text-white/70 hover:text-rose-light transition-colors"
                    onClick={() => link.href === '#contacto' && trackCTAClick('Contacto', 'Footer')}
                  >
                    {link.label}
                  </a>
                </li>)}
            </ul>
          </nav>

          {/* Legal */}
          <nav aria-label="Enlaces legales">
            <p className="font-semibold mb-4">Legal</p>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/aviso-legal" 
                  className="text-white/70 hover:text-rose-light transition-colors"
                  onMouseEnter={() => prefetchPage('LegalNotice')}
                >
                  Aviso legal
                </Link>
              </li>
              <li>
                <Link 
                  to="/politica-privacidad" 
                  className="text-white/70 hover:text-rose-light transition-colors"
                  onMouseEnter={() => prefetchPage('PrivacyPolicy')}
                >
                  Política de privacidad
                </Link>
              </li>
              <li>
                <Link 
                  to="/politica-cookies" 
                  className="text-white/70 hover:text-rose-light transition-colors"
                  onMouseEnter={() => prefetchPage('CookiesPolicy')}
                >
                  Política de cookies
                </Link>
              </li>
              <li>
                <button
                  type="button"
                  onClick={openCookieSettings}
                  className="text-white/70 hover:text-rose-light transition-colors"
                >
                  Configurar cookies
                </button>
              </li>
            </ul>
          </nav>
        </div>

        {/* Divider */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <p className="text-white/60 text-sm text-center">
              © {currentYear} Marina González Hurtado. Todos los derechos reservados.
            </p>
          </div>
          <p className="text-white/50 text-xs text-center mt-2">
            Diseñado por{' '}
            <a
              href="mailto:neuralpush@gmail.com"
              className="underline hover:text-rose-light transition-colors"
            >
              NeuralPush
            </a>
          </p>
        </div>
      </div>
    </footer>;
});

Footer.displayName = 'Footer';

export default Footer;